import { LeadActivity, LeadActivityPagination } from '@spike/client-model';
import { ClientsStatus, fetchLeadActivitiesThunk } from '@spike/clients-action';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import LeadActivityTable from './LeadActivityTable';

const DEFAULT_PAGE_SIZE = 10;

interface LeadActivitySectionProps {
    leadId: number;
    timeZone: string;
    refreshSignal?: number;
}

const LeadActivitySection: FunctionComponent<LeadActivitySectionProps> = ({ leadId, timeZone, refreshSignal }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

    const activities = useSelector<RootState, Array<LeadActivity> | undefined>(state => state.clients.leadActivities);
    const pagination = useSelector<RootState, LeadActivityPagination | undefined>(
        state => state.clients.leadActivitiesPagination
    );
    const status = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const fetchActivities = useCallback(
        (nextPage: number, nextSize: number) => {
            dispatch(fetchLeadActivitiesThunk(apiClientWrapper, leadId, nextPage, nextSize));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, leadId]
    );

    useEffect(() => {
        setPage(1);
        fetchActivities(1, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId, refreshSignal]);

    const pageChangeHandler = (nextPage: number) => {
        setPage(nextPage);
        fetchActivities(nextPage, size);
    };

    const sizeChangeHandler = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
        fetchActivities(1, nextSize);
    };

    return (
        <LeadActivityTable
            activities={activities ?? []}
            timeZone={timeZone}
            isLoading={status === ClientsStatus.LeadActivitiesFetching}
            hasError={status === ClientsStatus.LeadActivitiesError}
            page={page}
            size={size}
            totalPages={pagination?.totalPages ?? 1}
            onPageChange={pageChangeHandler}
            onSizeChange={sizeChangeHandler}
            onRetry={() => fetchActivities(page, size)}
        />
    );
};

export default LeadActivitySection;
